import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { usePremiumTokens } from '../theme/usePremiumTokens';
import { typography, radius, transitions } from '../theme/premiumTokens';

// Icons
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BuildIcon from '@mui/icons-material/Build';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import FenceIcon from '@mui/icons-material/Fence';
// AssessmentIcon removed - unused
import StraightenIcon from '@mui/icons-material/Straighten';
import PlaceIcon from '@mui/icons-material/Place';
import LinkIcon from '@mui/icons-material/Link';
import SendIcon from '@mui/icons-material/Send';
import EngineeringIcon from '@mui/icons-material/Engineering';
import GridViewIcon from '@mui/icons-material/GridView';
import ShareIcon from '@mui/icons-material/Share';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DevicesIcon from '@mui/icons-material/Devices';
// LockIcon removed - unused
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PeopleIcon from '@mui/icons-material/People';
import CampaignIcon from '@mui/icons-material/Campaign';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import TuneIcon from '@mui/icons-material/Tune';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import TodayIcon from '@mui/icons-material/Today';
import CalculateIcon from '@mui/icons-material/Calculate';
import RouteIcon from '@mui/icons-material/Route';
import PaymentIcon from '@mui/icons-material/Payment';
import HelpIcon from '@mui/icons-material/Help';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MapIcon from '@mui/icons-material/Map';
import StarIcon from '@mui/icons-material/Star';
import TimelineIcon from '@mui/icons-material/Timeline';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import NotesIcon from '@mui/icons-material/Notes';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DescriptionIcon from '@mui/icons-material/Description';
import SpeedIcon from '@mui/icons-material/Speed';
import NatureIcon from '@mui/icons-material/Nature';
import HistoryIcon from '@mui/icons-material/History';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import InsightsIcon from '@mui/icons-material/Insights';
import PhonelinkSetupIcon from '@mui/icons-material/PhonelinkSetup';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CategoryIcon from '@mui/icons-material/CategoryOutlined';
import LabelIcon from '@mui/icons-material/Label';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

import { useTranslation } from './LocalizationProvider';
import { sessionActions } from '../../store';
import {
 useAdministrator, useManager, useSuperAdmin, useRestriction 
} from '../util/permissions';
import useFeatures from '../util/useFeatures';
import LogoImage from '../auth/LogoImage';
import { useWidgetDock } from './WidgetDockContext';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CropFreeIcon from '@mui/icons-material/CropFree';
import { Typography } from '@mui/material';

// 2ECOMP Premium: TopBar height (touch-friendly)
export const TOPBAR_HEIGHT = 48;

// Safe area inset for notched devices (iPhone X+)
const SAFE_AREA_TOP = 'env(safe-area-inset-top, 0px)';

// 2ECOMP Premium: Cores centralizadas usando tokens dinâmicos (com safe fallbacks)
const getThemeColors = (tokens) => ({
  // Header background - Glassmorphism
  headerBg: tokens?.glassmorphism?.heavy?.background || 'rgba(13, 17, 23, 0.85)',
  headerBorder: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
  // Text colors
  text: tokens?.text?.secondary || '#8b949e',
  textHover: tokens?.text?.primary || '#f0f6fc',
  textActive: tokens?.accent?.primary || '#00d4aa',
  // Icon colors
  iconColor: tokens?.text?.secondary || '#8b949e',
  iconHover: tokens?.text?.primary || '#f0f6fc',
  iconActive: tokens?.accent?.primary || '#00d4aa',
  // Dropdown menu - Glassmorphism (fundo escuro, texto claro)
  dropdownBg: tokens?.glassmorphism?.medium?.background || 'rgba(22, 27, 34, 0.95)',
  dropdownHover: tokens?.surface?.glassHover || 'rgba(255, 255, 255, 0.12)',
  dropdownBorder: tokens?.border?.default || 'rgba(255, 255, 255, 0.1)',
  // Menu text - sempre claro pois o fundo do dropdown é escuro
  menuText: '#f1f5f9',           // Slate 100 - texto principal do menu
  menuTextHover: '#ffffff',      // Branco no hover
  menuIcon: '#cbd5e1',           // Slate 300 - ícones do menu
  menuIconHover: '#00d4aa',      // Accent no hover
  // Shadow
  shadow: 'rgba(0, 0, 0, 0.4)',
});

const useStyles = makeStyles()((theme, { tokens }) => {
  const colors = getThemeColors(tokens);

  // Safe token access for internal usage
  const safeTokens = {
    surface: {
      glassHover: tokens?.surface?.glassHover || 'rgba(255, 255, 255, 0.08)',
    },
    accent: {
      primary: tokens?.accent?.primary || '#00d4aa',
      subtle: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
      hover: tokens?.accent?.hover || 'rgba(0, 212, 170, 0.15)',
      glow: tokens?.accent?.glow || 'rgba(0, 212, 170, 0.3)',
    },
    text: {
      secondary: tokens?.text?.secondary || '#8b949e',
      muted: tokens?.text?.muted || '#6e7681',
    },
    border: {
      subtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
    },
    shadows: {
      xl: tokens?.shadows?.xl || '0 20px 40px rgba(0, 0, 0, 0.4)',
      inset: tokens?.shadows?.inset || 'inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    },
  };

  return {
    // 2ECOMP Premium: Header container (transparent, apenas posicionamento)
    // Safe area support for notched devices
    header: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      // Add safe area top to height
      height: `calc(${TOPBAR_HEIGHT}px + ${SAFE_AREA_TOP})`,
      paddingTop: SAFE_AREA_TOP,
      zIndex: theme.zIndex.drawer + 1,
      pointerEvents: 'none',
      '@media print': {
        display: 'none',
      },
    },
    // 2ECOMP Premium: Logo container com corte diagonal + glassmorphism
    // Safe area support - offset by safe area top
    logoContainer: {
      position: 'absolute',
      top: SAFE_AREA_TOP,
      left: 0,
      height: TOPBAR_HEIGHT,
      // Glassmorphism - blur alinhado com tokens (20px)
      backgroundColor: colors.headerBg,
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderBottom: `1px solid ${colors.headerBorder}`,
      display: 'flex',
      alignItems: 'center',
      padding: '0 16px',
      zIndex: 2,
      pointerEvents: 'auto',
      cursor: 'pointer',
      transition: transitions.all,
      // Corte diagonal direito - Desktop (apenas fill, sem borda)
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: '100%',
        width: 0,
        height: 0,
        borderBottom: `${TOPBAR_HEIGHT}px solid transparent`,
        borderLeft: `60px solid ${colors.headerBg}`,
        zIndex: 2,
      },
      '&:hover': {
        opacity: 0.9,
      },
      // Mobile: corte menor para não espremer a logo
      [theme.breakpoints.down('sm')]: {
        padding: '0 12px',
        '&::after': {
          borderLeft: `30px solid ${colors.headerBg}`,
        },
      },
    },
    logo: {
      height: 28,
      maxWidth: 160,
      objectFit: 'contain',
      cursor: 'pointer',
    },
    // 2ECOMP Premium: Nav container com corte diagonal + glassmorphism
    // Safe area support - offset by safe area top
    navContainer: {
      position: 'absolute',
      top: SAFE_AREA_TOP,
      right: 0,
      height: TOPBAR_HEIGHT,
      // Glassmorphism - blur alinhado com tokens (20px)
      backgroundColor: colors.headerBg,
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderBottom: `1px solid ${colors.headerBorder}`,
      display: 'flex',
      alignItems: 'center',
      padding: '0 12px',
      zIndex: 2,
      pointerEvents: 'auto',
      // Corte diagonal esquerdo - Desktop (apenas fill, sem borda)
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        right: '100%',
        width: 0,
        height: 0,
        borderBottom: `${TOPBAR_HEIGHT}px solid transparent`,
        borderRight: `60px solid ${colors.headerBg}`,
        zIndex: 2,
      },
      // Mobile: corte menor e proporcional
      [theme.breakpoints.down('sm')]: {
        padding: '0 8px',
        '&::after': {
          borderRight: `30px solid ${colors.headerBg}`,
        },
      },
    },
    navIcons: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    // 2ECOMP Premium: Nav icon com 44px touch target
    navIcon: {
      color: colors.iconColor,
      minWidth: 40,
      minHeight: 40,
      padding: '8px',
      borderRadius: radius.md,
      transition: transitions.all,
      '&:hover': {
        backgroundColor: safeTokens.surface.glassHover,
        color: colors.iconHover,
      },
      '&:focus-visible': {
        outline: `2px solid ${safeTokens.accent.primary}`,
        outlineOffset: 2,
      },
      '& .MuiSvgIcon-root': {
        fontSize: '20px',
      },
    },
    // 2ECOMP Premium: Nav icon ativo
    navIconActive: {
      color: colors.iconActive,
      backgroundColor: safeTokens.accent.subtle,
      '&:hover': {
        backgroundColor: safeTokens.accent.hover,
        color: colors.iconActive,
      },
    },
    // 2ECOMP Premium: Menu dropdown com glassmorphism
    menuPaper: {
      backgroundColor: colors.dropdownBg,
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      border: `1px solid ${colors.dropdownBorder}`,
      borderRadius: radius.lg,
      boxShadow: `${safeTokens.shadows.xl}, ${safeTokens.shadows.inset}`,
      minWidth: '200px',
      maxHeight: '80vh',
      overflowY: 'auto',
      marginTop: 4,
    },
    // 2ECOMP Premium: Menu item com hover accent (texto claro no fundo escuro)
    menuItem: {
      fontSize: '0.8125rem',
      fontFamily: typography.fontFamily.body,
      padding: '10px 16px',
      color: colors.menuText,
      borderRadius: radius.sm,
      margin: '2px 6px',
      minHeight: 40,
      transition: transitions.all,
      '&:hover': {
        backgroundColor: safeTokens.accent.subtle,
        color: colors.menuTextHover,
        '& .MuiListItemIcon-root': {
          color: colors.menuIconHover,
        },
      },
      '&:focus-visible': {
        outline: `2px solid ${safeTokens.accent.primary}`,
        outlineOffset: -2,
      },
    },
    // 2ECOMP Premium: Menu icon (claro no fundo escuro)
    menuIcon: {
      minWidth: '32px',
      color: colors.menuIcon,
      '& .MuiSvgIcon-root': {
        fontSize: '18px',
      },
    },
    // 2ECOMP Premium: Divider sutil
    menuDivider: {
      borderColor: safeTokens.border.subtle,
      margin: '6px 12px',
    },
    // 2ECOMP Premium: Subheader com tipografia refinada (mais visível no fundo escuro)
    menuSubheader: {
      fontSize: '0.65rem',
      fontWeight: 600,
      fontFamily: typography.fontFamily.heading,
      color: '#94a3b8',  // Slate 400 - mais visível no fundo escuro
      padding: '12px 16px 6px',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    },
    mobileOnly: {
      display: 'flex',
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },
    desktopOnly: {
      display: 'none',
      [theme.breakpoints.up('md')]: {
        display: 'flex',
      },
    },
    // 2ECOMP Premium: Mobile menu icon com 44px touch target
    mobileMenuIcon: {
      minWidth: 44,
      minHeight: 44,
      padding: 10,
      '& .MuiSvgIcon-root': {
        fontSize: 24,
      },
    },
    // 2ECOMP Premium: Widget Dock Area
    widgetDock: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      flex: 1,
      height: TOPBAR_HEIGHT,
      padding: '0 16px',
      overflow: 'hidden',
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
    // 2ECOMP Premium: Docked chip com glassmorphism
    dockedChip: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '6px 12px',
      borderRadius: radius.full,
      background: 'rgba(255, 255, 255, 0.05)',
      border: `1px solid ${safeTokens.border.subtle}`,
      cursor: 'pointer',
      transition: transitions.all,
      height: 32,
      '&:hover': {
        background: safeTokens.accent.subtle,
        borderColor: safeTokens.accent.primary,
        transform: 'scale(1.02)',
        boxShadow: `0 0 12px ${safeTokens.accent.glow}`,
      },
    },
    dockedChipIcon: {
      fontSize: 16,
      color: safeTokens.text.secondary,
    },
    dockedChipLabel: {
      fontSize: '0.72rem',
      fontWeight: 500,
      fontFamily: typography.fontFamily.body,
      color: safeTokens.text.secondary,
      whiteSpace: 'nowrap',
    },
    // 2ECOMP Premium: Chip value com accent
    dockedChipValue: {
      fontSize: '0.75rem',
      fontWeight: 700,
      fontFamily: typography.fontFamily.mono,
      color: safeTokens.accent.primary,
    },
  };
});

const TopBar = () => {
  const tokens = usePremiumTokens();
  const { classes } = useStyles({ tokens });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const t = useTranslation();

  const admin = useAdministrator();
  const manager = useManager();
  const superAdmin = useSuperAdmin();
  const readonly = useRestriction('readonly');
  const disableReports = useRestriction('disableReports');
  const features = useFeatures();

  const user = useSelector((state) => state.session.user);
  const userId = user?.id;
  const supportLink = useSelector((state) => state.session.server?.attributes?.support);
  const billingLink = user?.attributes?.billingLink;

  // Get selected device from Redux (for pre-selecting in reports)
  const selectedDeviceId = useSelector((state) => state.devices.selectedId);

  // Build report link with device pre-selection (similar to ReportsMenu)
  const buildReportLink = useCallback((path) => {
    // First check existing URL params
    const sourceParams = new URLSearchParams(location.search);
    const existingDeviceIds = sourceParams.getAll('deviceId');
    const existingGroupIds = sourceParams.getAll('groupId');

    // If already has device/group selection from URL, preserve it
    if (existingDeviceIds.length || existingGroupIds.length) {
      const params = new URLSearchParams();
      // For single device reports (chart, route, replay), use first device only
      if (path === '/reports/chart' || path === '/reports/route' || path === '/replay') {
        if (existingDeviceIds[0]) params.append('deviceId', existingDeviceIds[0]);
      } else {
        existingDeviceIds.forEach((id) => params.append('deviceId', id));
        existingGroupIds.forEach((id) => params.append('groupId', id));
      }
      const search = params.toString();
      return search ? `${path}?${search}` : path;
    }

    // If no URL params but has selected device from map, use it
    if (selectedDeviceId) {
      return `${path}?deviceId=${selectedDeviceId}`;
    }

    return path;
  }, [location.search, selectedDeviceId]);

  // Menu anchors
  const [adminAnchor, setAdminAnchor] = useState(null);
  const [dashboardAnchor, setDashboardAnchor] = useState(null);
  const [reportsAnchor, setReportsAnchor] = useState(null);
  const [routingAnchor, setRoutingAnchor] = useState(null);
  const [toolsAnchor, setToolsAnchor] = useState(null);
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [mobileAnchor, setMobileAnchor] = useState(null);

  const closeAll = () => {
    setAdminAnchor(null);
    setDashboardAnchor(null);
    setReportsAnchor(null);
    setRoutingAnchor(null);
    setToolsAnchor(null);
    setAccountAnchor(null);
    setMobileAnchor(null);
  };

  const handleLogout = async () => {
    closeAll();
    const notificationToken = window.localStorage.getItem('notificationToken');
    if (notificationToken && !user?.readonly) {
      window.localStorage.removeItem('notificationToken');
      const tokens = user?.attributes?.notificationTokens?.filter((it) => it !== notificationToken) || [];
      await fetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...user, attributes: { ...user.attributes, notificationTokens: tokens } }),
      });
    }
    await fetch('/api/session', { method: 'DELETE' });
    dispatch(sessionActions.updateUser(null));
    navigate('/login');
  };

  const navigateTo = (path, isReport = false) => {
    closeAll();
    navigate(isReport ? buildReportLink(path) : path);
  };

  // Menu definitions

  // Admin Menu (admin/manager only)
  const adminMenuItems = [
    { id: 'server', icon: <SettingsIcon />, label: t('settingsServer'), path: '/settings/server', show: admin },
    { id: 'users', icon: <PeopleIcon />, label: t('settingsUsers'), path: '/settings/users' },
    { id: 'announcement', icon: <CampaignIcon />, label: t('serverAnnouncement'), path: '/settings/announcement' },
    { id: 'divider-admin1', divider: true },
    { id: 'statistics', icon: <BarChartIcon />, label: t('statisticsTitle'), path: '/reports/statistics', show: admin },
    { id: 'audit', icon: <VerifiedUserIcon />, label: t('reportAudit'), path: '/reports/audit', show: admin },
    { id: 'configurator', icon: <SettingsRemoteIcon />, label: t('configuratorTitle'), path: '/settings/configurator', show: admin },
    { id: 'emulator', icon: <PhonelinkSetupIcon />, label: t('sharedEmulator'), path: '/emulator', show: admin },
    { id: 'divider-admin2', divider: true, show: superAdmin },
    { id: 'tags', icon: <LabelIcon />, label: t('tagsAdmin'), path: '/settings/tags', show: superAdmin },
    { id: 'licenses', icon: <VpnKeyIcon />, label: t('settingsLicenses') || 'Licenses', path: '/settings/licenses', show: superAdmin },
  ];

  // Dashboards Menu (all users)
  const dashboardMenuItems = [
    { id: 'map', icon: <MapIcon />, label: t('mapLive') || t('mapTitle'), path: '/' },
    { id: 'overview', icon: <GridViewIcon />, label: 'Overview', path: '/overview' },
    { id: 'fleet', icon: <LocalShippingIcon />, label: t('fleetDashboard'), path: '/fleet' },
    { id: 'temperature', icon: <ThermostatIcon />, label: t('temperatureMonitor'), path: '/temperature' },
    { id: 'divider-dash1', divider: true },
    { id: 'premium', icon: <StarIcon />, label: 'Premium', path: '/dashboard-premium' },
  ];

  // Relatórios - Organizados por categoria funcional
  // isReport: true enables device pre-selection from map
  const reportsMenuItems = [
    // === OPERACIONAL (uso diário) ===
    { id: 'operational-header', subheader: t('reportOperational') || 'Operacional' },
    { id: 'combined', icon: <StarIcon />, label: t('reportCombined'), path: '/reports/combined', isReport: true },
    { id: 'events', icon: <NotificationsActiveIcon />, label: t('reportEvents'), path: '/reports/events', isReport: true },
    { id: 'replay', icon: <RouteIcon />, label: t('reportReplay'), path: '/replay', isReport: true },
    // === VIAGENS ===
    { id: 'divider-trips', divider: true },
    { id: 'trips-header', subheader: t('reportTrips') },
    { id: 'trips', icon: <PlayCircleFilledIcon />, label: t('reportTrips'), path: '/reports/trips', isReport: true },
    { id: 'stops', icon: <PauseCircleFilledIcon />, label: t('reportStops'), path: '/reports/stops', isReport: true },
    { id: 'summary', icon: <FormatListBulletedIcon />, label: t('reportSummary'), path: '/reports/summary', isReport: true },
    { id: 'route', icon: <TimelineIcon />, label: t('reportPositions'), path: '/reports/route', isReport: true },
    // === ANÁLISE E VISUALIZAÇÃO ===
    { id: 'divider-analysis', divider: true },
    { id: 'analysis-header', subheader: t('reportAnalysis') || 'Análise' },
    { id: 'chart', icon: <TrendingUpIcon />, label: t('reportChart'), path: '/reports/chart', isReport: true },
    { id: 'heatmap', icon: <WhatshotIcon />, label: t('reportHeatmap'), path: '/heatmap', isReport: true },
    { id: 'eventmap', icon: <MapIcon />, label: t('mapEventTitle'), path: '/eventmap', isReport: true },
    { id: 'correlation', icon: <LinkIcon />, label: t('correlationTitle'), path: '/reports/correlation', isReport: true },
    { id: 'geofences-report', icon: <FenceIcon />, label: t('sharedGeofences'), path: '/reports/geofences', isReport: true },
    // === GESTÃO DE FROTA ===
    { id: 'divider-mgmt', divider: true },
    { id: 'mgmt-header', subheader: t('reportManagement') || 'Gestão' },
    { id: 'mgmt-speeding', icon: <SpeedIcon />, label: t('reportSpeeding'), path: '/reports/management/speeding', isReport: true },
    { id: 'mgmt-eco', icon: <NatureIcon />, label: t('reportEcoDriving'), path: '/reports/management/eco', isReport: true },
    { id: 'mgmt-trips', icon: <RouteIcon />, label: t('reportTripsAnalysis'), path: '/reports/management/trips', isReport: true },
    { id: 'mgmt-driver', icon: <PersonIcon />, label: t('reportDriverPerformance'), path: '/reports/management/driver', isReport: true },
    { id: 'costs', icon: <AttachMoneyIcon />, label: t('reportCosts'), path: '/reports/costs', isReport: true },
    { id: 'costsresume', icon: <AttachMoneyIcon />, label: t('reportCostsResume'), path: '/reports/costsresume', isReport: true },
    // === SISTEMA ===
    { id: 'divider-system', divider: true },
    { id: 'system-header', subheader: t('reportSystem') || 'Sistema' },
    { id: 'actions', icon: <HistoryIcon />, label: t('reportActions'), path: '/reports/actions' },
    { id: 'devices-report', icon: <DevicesIcon />, label: t('deviceTitle'), path: '/reports/devices' },
    { id: 'commands-report', icon: <SendIcon />, label: t('reportCommands'), path: '/reports/commands' },
    { id: 'logs', icon: <NotesIcon />, label: t('sharedLogs'), path: '/reports/logs' },
    { id: 'scheduled', icon: <EventRepeatIcon />, label: t('reportScheduled'), path: '/reports/scheduled', show: !readonly },
  ];

  // Routing Menu (NEW - !readonly)
  const routingMenuItems = [
    { id: 'route-planning', icon: <AltRouteIcon />, label: t('routePlanning'), path: '/settings/route-planning' },
    { id: 'route-execution', icon: <PlayCircleFilledIcon />, label: t('routeExecution') || 'Execução de Rotas', path: '/settings/route-execution' },
    { id: 'operations-center', icon: <GridViewIcon />, label: t('operationsCenter'), path: '/settings/operations-center' },
    { id: 'divider-routing1', divider: true },
    { id: 'routing-dashboard', icon: <InsightsIcon />, label: t('routingDashboard'), path: '/settings/routing-dashboard' },
    { id: 'driver-activity', icon: <LocalShippingIcon />, label: t('driverActivityDashboard'), path: '/settings/driver-activity' },
    { id: 'driver-score', icon: <SpeedIcon />, label: t('driverScoreDashboard'), path: '/settings/driver-score' },
    { id: 'divider-routing2', divider: true },
    { id: 'routes-library', icon: <LocalLibraryIcon />, label: t('sharedRoutes'), path: '/settings/routes' },
    { id: 'driver-app', icon: <SmartToyIcon />, label: t('driverApp'), path: '/driver' },
  ];

  // Tools Menu (!readonly) - organized by sections
  const toolsMenuItems = [
    // Location section
    { id: 'location-header', subheader: t('sharedLocation') || 'Location' },
    { id: 'geofences', icon: <FenceIcon />, label: t('sharedGeofences'), path: '/geofences', show: !readonly },
    { id: 'pois', icon: <PlaceIcon />, label: t('poiTitle'), path: '/settings/pois', show: !readonly },
    { id: 'poigroups', icon: <CategoryIcon />, label: t('poiGroups'), path: '/settings/poigroups', show: !readonly },
    { id: 'correlations', icon: <LinkIcon />, label: t('sharedCorrelations'), path: '/settings/correlations', show: !readonly },
    // Fleet section
    { id: 'divider-tools1', divider: true },
    { id: 'fleet-header', subheader: t('fleetDashboard') || 'Fleet' },
    { id: 'devices', icon: <DevicesIcon />, label: t('deviceTitle'), path: '/settings/devices', show: !readonly },
    { id: 'groups', icon: <FolderIcon />, label: t('settingsGroups'), path: '/settings/groups', show: !readonly && !features.disableGroups },
    { id: 'drivers', icon: <PersonIcon />, label: t('sharedDrivers'), path: '/settings/drivers', show: !readonly && !features.disableDrivers },
    // Automation section
    { id: 'divider-tools2', divider: true },
    { id: 'automation-header', subheader: t('sharedNotifications') || 'Automation' },
    { id: 'notifications', icon: <NotificationsActiveIcon />, label: t('sharedNotifications'), path: '/settings/notifications', show: !readonly },
    { id: 'commands', icon: <SendIcon />, label: t('sharedSavedCommands'), path: '/settings/commands', show: !readonly && !features.disableSavedCommands },
    { id: 'maintenance', icon: <EngineeringIcon />, label: t('sharedMaintenance'), path: '/settings/maintenances', show: !readonly && !features.disableMaintenance },
    { id: 'maintenance-dashboard', icon: <BuildIcon />, label: t('maintenanceDashboard'), path: '/settings/maintenance-dashboard', show: !readonly && !features.disableMaintenance },
    // Advanced section
    { id: 'divider-tools3', divider: true },
    { id: 'advanced-header', subheader: t('sharedAdvanced') || 'Advanced' },
    { id: 'attributes', icon: <CalculateIcon />, label: t('sharedComputedAttributes'), path: '/settings/attributes', show: !readonly && !features.disableComputedAttributes },
    { id: 'calendars', icon: <TodayIcon />, label: t('sharedCalendars'), path: '/settings/calendars', show: !readonly && !features.disableCalendars },
    { id: 'costs', icon: <AttachMoneyIcon />, label: t('sharedCosts'), path: '/settings/costs', show: !readonly },
    // Utilities section
    { id: 'divider-tools4', divider: true },
    { id: 'utilities-header', subheader: t('sharedTools') || 'Utilities' },
    { id: 'ruler', icon: <StraightenIcon />, label: t('sharedRuler'), path: '/' },
    { id: 'share', icon: <ShareIcon />, label: t('deviceShare'), path: '/', show: !readonly },
    { id: 'assistant', icon: <SmartToyIcon />, label: t('assistantTitle'), path: '/assistant' },
  ];

  // Account Menu (all users)
  const accountMenuItems = [
    { id: 'user-header', subheader: user?.name || user?.email || 'Usuário' },
    { id: 'user', icon: <PersonIcon />, label: t('settingsUser'), path: `/settings/user/${userId}` },
    { id: 'preferences', icon: <TuneIcon />, label: t('sharedPreferences'), path: '/settings/preferences' },
    { id: 'dashboard-settings', icon: <GridViewIcon />, label: t('dashboardMenuText'), path: '/settings/dashboard' },
    { id: 'divider-acc1', divider: true },
    { id: 'billing', icon: <PaymentIcon />, label: t('userBilling'), path: billingLink, show: !!billingLink, external: true },
    { id: 'support', icon: <HelpIcon />, label: t('settingsSupport'), path: supportLink, show: !!supportLink, external: true },
    { id: 'divider-acc2', divider: true, show: !!billingLink || !!supportLink },
    { id: 'logout', icon: <LogoutIcon />, label: t('loginLogout'), action: handleLogout },
  ];

  const renderMenuItems = (items, closeHandler) => (
    items
      .filter((item) => item.show !== false)
      .map((item) => {
        if (item.divider) {
          return <Divider key={item.id} className={classes.menuDivider} />;
        }
        if (item.subheader) {
          return <Box key={item.id} className={classes.menuSubheader}>{item.subheader}</Box>;
        }
        return (
          <MenuItem
            key={item.id}
            onClick={() => {
              if (item.action) {
                item.action();
              } else if (item.external) {
                window.open(item.path, '_blank');
                closeHandler();
              } else {
                closeHandler();
                navigateTo(item.path, item.isReport);
              }
            }}
            className={classes.menuItem}
          >
            <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '13px' }} />
          </MenuItem>
        );
      })
  );

  // Widget Dock - get docked widgets
  const { dockedChips, expandWidget, getWidgetData } = useWidgetDock();

  // Icon mapping for docked widgets
  const getWidgetIcon = (iconName) => {
    const icons = {
      DirectionsCar: <DirectionsCarIcon className={classes.dockedChipIcon} />,
      Place: <PlaceIcon className={classes.dockedChipIcon} />,
      CropFree: <CropFreeIcon className={classes.dockedChipIcon} />,
      Speed: <SpeedIcon className={classes.dockedChipIcon} />,
    };
    return icons[iconName] || <DirectionsCarIcon className={classes.dockedChipIcon} />;
  };

  return (
    <>
      {/* 2ECOMP Premium: Header com cortes diagonais + glassmorphism */}
      <Box className={classes.header}>
        {/* Logo - Left com corte diagonal */}
        <Box className={classes.logoContainer} onClick={() => navigate('/')}>
          <LogoImage className={classes.logo} height={28} />
        </Box>

        {/* Widget Dock Area - Center */}
        <Box className={classes.widgetDock}>
          {dockedChips.map((widget) => {
            const widgetData = getWidgetData(widget.id);
            return (
              <Tooltip key={widget.id} title={`Expandir ${widget.label}`}>
                <Box
                  className={classes.dockedChip}
                  onClick={() => expandWidget(widget.id)}
                >
                  {getWidgetIcon(widget.icon)}
                  <Typography className={classes.dockedChipLabel}>
                    {widget.label}
                  </Typography>
                  {widgetData?.value !== undefined && (
                    <Typography className={classes.dockedChipValue}>
                      {widgetData.value}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* Nav Icons - Right com corte diagonal */}
        <Box className={classes.navContainer}>
          {/* Desktop */}
          <Box className={`${classes.navIcons} ${classes.desktopOnly}`}>
            {manager && (
              <Tooltip title="Admin">
                <IconButton className={classes.navIcon} onClick={(e) => setAdminAnchor(e.currentTarget)}>
                  <AdminPanelSettingsIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('dashboardMenuText')}>
              <IconButton className={classes.navIcon} onClick={(e) => setDashboardAnchor(e.currentTarget)}>
                <GridViewIcon />
              </IconButton>
            </Tooltip>
            {!disableReports && (
              <Tooltip title={t('reportTitle')}>
                <IconButton className={classes.navIcon} onClick={(e) => setReportsAnchor(e.currentTarget)}>
                  <DescriptionIcon />
                </IconButton>
              </Tooltip>
            )}
            {!readonly && (
              <Tooltip title={t('routePlanning')}>
                <IconButton className={classes.navIcon} onClick={(e) => setRoutingAnchor(e.currentTarget)}>
                  <AltRouteIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('sharedTools')}>
              <IconButton className={classes.navIcon} onClick={(e) => setToolsAnchor(e.currentTarget)}>
                <BuildIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('userMyAccount')}>
              <IconButton className={classes.navIcon} onClick={(e) => setAccountAnchor(e.currentTarget)}>
                <AccountCircleIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Mobile - Menu hamburger visível em telas < md (900px) */}
          <Box className={classes.mobileOnly}>
            <IconButton
              className={`${classes.navIcon} ${classes.mobileMenuIcon}`}
              onClick={(e) => setMobileAnchor(e.currentTarget)}
              aria-label="menu"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Admin Menu */}
      {manager && (
        <Menu
          anchorEl={adminAnchor}
          open={Boolean(adminAnchor)}
          onClose={() => setAdminAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ className: classes.menuPaper }}
        >
          {renderMenuItems(adminMenuItems, () => setAdminAnchor(null))}
        </Menu>
      )}

      {/* Dashboard Menu */}
      <Menu
        anchorEl={dashboardAnchor}
        open={Boolean(dashboardAnchor)}
        onClose={() => setDashboardAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.menuPaper }}
      >
        {renderMenuItems(dashboardMenuItems, () => setDashboardAnchor(null))}
      </Menu>

      {/* Reports Menu */}
      {!disableReports && (
        <Menu
          anchorEl={reportsAnchor}
          open={Boolean(reportsAnchor)}
          onClose={() => setReportsAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ className: classes.menuPaper }}
        >
          {renderMenuItems(reportsMenuItems, () => setReportsAnchor(null))}
        </Menu>
      )}

      {/* Routing Menu */}
      {!readonly && (
        <Menu
          anchorEl={routingAnchor}
          open={Boolean(routingAnchor)}
          onClose={() => setRoutingAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ className: classes.menuPaper }}
        >
          {renderMenuItems(routingMenuItems, () => setRoutingAnchor(null))}
        </Menu>
      )}

      {/* Tools Menu */}
      <Menu
        anchorEl={toolsAnchor}
        open={Boolean(toolsAnchor)}
        onClose={() => setToolsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.menuPaper }}
      >
        {renderMenuItems(toolsMenuItems, () => setToolsAnchor(null))}
      </Menu>

      {/* Account Menu */}
      <Menu
        anchorEl={accountAnchor}
        open={Boolean(accountAnchor)}
        onClose={() => setAccountAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.menuPaper }}
      >
        {renderMenuItems(accountMenuItems, () => setAccountAnchor(null))}
      </Menu>

      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileAnchor}
        open={Boolean(mobileAnchor)}
        onClose={() => setMobileAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.menuPaper, sx: { minWidth: '220px' } }}
      >
        {[
          // Admin section (conditional)
          ...(manager ? [
            <Box key="admin-header" className={classes.menuSubheader}>Admin</Box>,
            ...adminMenuItems.filter((item) => !item.divider && item.show !== false).slice(0, 3).map((item) => (
              <MenuItem
                key={item.id}
                onClick={() => { setMobileAnchor(null); navigate(item.path); }}
                className={classes.menuItem}
              >
                <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '13px' }} />
              </MenuItem>
            )),
            <Divider key="admin-divider" className={classes.menuDivider} />,
          ] : []),
          // Dashboard section
          <Box key="dashboard-header" className={classes.menuSubheader}>{t('dashboardMenuText')}</Box>,
          ...dashboardMenuItems.filter(i => !i.divider && i.show !== false).slice(0, 4).map((item) => (
            <MenuItem
              key={item.id}
              onClick={() => { setMobileAnchor(null); navigate(item.path); }}
              className={classes.menuItem}
            >
              <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '13px' }} />
            </MenuItem>
          )),
          <Divider key="dashboard-divider" className={classes.menuDivider} />,
          // Routing section (conditional)
          ...(!readonly ? [
            <Box key="routing-header" className={classes.menuSubheader}>{t('routePlanning')}</Box>,
            ...routingMenuItems.filter(i => !i.divider && i.show !== false).slice(0, 4).map((item) => (
              <MenuItem
                key={item.id}
                onClick={() => { setMobileAnchor(null); navigate(item.path); }}
                className={classes.menuItem}
              >
                <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '13px' }} />
              </MenuItem>
            )),
            <Divider key="routing-divider" className={classes.menuDivider} />,
          ] : []),
          // Reports section (conditional)
          ...(!disableReports ? [
            <Box key="reports-header" className={classes.menuSubheader}>{t('reportTitle')}</Box>,
            ...reportsMenuItems.filter(i => !i.divider && !i.subheader && i.show !== false).slice(0, 6).map((item) => (
              <MenuItem
                key={item.id}
                onClick={() => { setMobileAnchor(null); navigate(item.path); }}
                className={classes.menuItem}
              >
                <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '13px' }} />
              </MenuItem>
            )),
            <Divider key="reports-divider" className={classes.menuDivider} />,
          ] : []),
          // Tools section
          <Box key="tools-header" className={classes.menuSubheader}>{t('sharedTools')}</Box>,
          ...toolsMenuItems.filter(i => !i.divider && !i.subheader && i.show !== false).slice(0, 5).map((item) => (
            <MenuItem
              key={item.id}
              onClick={() => { setMobileAnchor(null); navigate(item.path); }}
              className={classes.menuItem}
            >
              <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '13px' }} />
            </MenuItem>
          )),
          <Divider key="tools-divider" className={classes.menuDivider} />,
          // Account section
          <Box key="account-header" className={classes.menuSubheader}>{t('userMyAccount')}</Box>,
          ...accountMenuItems.filter(i => !i.divider && !i.subheader && i.show !== false).map((item) => (
            <MenuItem
              key={item.id}
              onClick={() => {
                if (item.action) {
                  item.action();
                } else if (item.external) {
                  window.open(item.path, '_blank');
                  setMobileAnchor(null);
                } else {
                  setMobileAnchor(null);
                  navigate(item.path);
                }
              }}
              className={classes.menuItem}
            >
              <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '13px' }} />
            </MenuItem>
          )),
        ]}
      </Menu>
    </>
  );
};

export default TopBar;
